import React, {
    useCallback, useMemo, useRef, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { toggleProfile } from 'features/profile/editProfileSlice'
import { myOrganizationsSelector } from 'features/auth/authSlice'
import { useClickOutSide } from 'hooks'
import { RetrieveUserResponse } from 'types/swagger'
import { _, colors, formatName } from 'utils'
import Icon from '../../Icon'
import InfoItem from '../../InfoItem'
import OrganizationListModal from './OrganizationListModal'

const S = {
    UserInfoWrapper: styled.div`
        position: relative;
        display: flex;
        align-items: center;
        cursor: pointer;
    `,
    UserInfo: styled(InfoItem)`
        width: 200px;
        & > div {
            flex-direction: row-reverse;
            height: 35px;
            .info-wrapper {
                margin-right: 8px;
                text-align: right;
                &__description {
                    font-size: 12px;
                    text-transform: uppercase;
                }
            }
        }
    `,
    ChevronDown: styled(Icon)<{ showMenu: boolean }>`
        margin-left: -8px;
        transform: rotate(${({ showMenu }) => (showMenu ? '-180deg' : 0)});
        transition: 0.3s;
    `,
    Menu: styled.div<{ showMenu: boolean }>`
        position: absolute;
        bottom: -8px;
        right: 0;
        min-width: 190px;
        max-width: 300px;
        display: ${({ showMenu }) => (showMenu ? 'block' : 'none')};
        padding: 8px 0;
        background: #FFFFFF;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
        border-radius: 2px;
        overflow: hidden;
        transform: translateY(100%);
        transition: opacity 0.3s;
    `,
    MenuItem: styled.div`
        display: flex;
        align-items: center;
        padding: 8px;
        font-size: 14px;
        color: #96A1A9;
        white-space: nowrap;
        & > span {
            margin-bottom: 3px;
            margin-right: 8px;
        }
        &:hover {
            background-color: ${colors.light};
        }
    `,
    Divider: styled.div`
        margin: 8px 0;
        width: 100%;
        height: 0;
        border-top: 1px solid #F0F0F0;
    `
}

interface Props {
    user: RetrieveUserResponse
    isReseller?: boolean
}

const UserInfo = ({ user, isReseller }: Props) => {
    const dispatch = useDispatch()
    const [showMenu, setShowMenu] = useState(false)
    const [showOrganisationsModal, setShowOrganisationsModal] = useState(false)
    const menuRef = useRef<HTMLDivElement>()
    useClickOutSide(menuRef, () => showMenu && setShowMenu(false))
    const myOrganizations = useSelector(myOrganizationsSelector)

    const isOrganizationAdmin = useMemo(() => myOrganizations?.length > 0, [myOrganizations])

    const handleShowOrganisationsModal = useCallback(() => {
        setShowOrganisationsModal(true)
    }, [])

    const handleCloseOrganisationsModal = useCallback(() => {
        setShowOrganisationsModal(false)
    }, [])

    return (
        <S.UserInfoWrapper onClick={() => setShowMenu(!showMenu)} ref={menuRef}>
            <S.UserInfo
                image={user?.mugshot}
                name={formatName(user)}
                description={isReseller ? _('top_navigation.is_reseller') : ''}
            />
            <S.ChevronDown
                icon="chevronDown"
                size={18}
                color={colors.iconOpacity(0.5)}
                showMenu={showMenu}
            />
            <S.Menu showMenu={showMenu}>
                <S.MenuItem onClick={() => dispatch(toggleProfile())}>
                    <Icon icon="account" size={18} />
                    {_('top_navigation.user_info')}
                </S.MenuItem>
                <S.Divider />
                {isOrganizationAdmin && (
                    <>
                        <S.MenuItem onClick={handleShowOrganisationsModal}>
                            <Icon icon="organisation" size={18} />
                            {_('top_navigation.manage_organisation')}
                        </S.MenuItem>
                        <S.Divider />
                    </>
                )}
                <Link to="/logout" style={{ textDecoration: 'none' }}>
                    <S.MenuItem>
                        <Icon icon="logout" size={18} />
                        {_('top_navigation.logout')}
                    </S.MenuItem>
                </Link>
            </S.Menu>
            <OrganizationListModal
                show={showOrganisationsModal}
                handleClose={handleCloseOrganisationsModal}
            />
        </S.UserInfoWrapper>
    )
}

export default UserInfo
